import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Link2, RefreshCw, Unlink } from 'lucide-react';
import { api, errorMessage, post } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { Alert, Badge, Button, Card, CardHeader, PageHeader, Spinner } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import type { TenantRestaurant } from '../TenantApp';

type Integration = {
  platformConfigured: boolean; enabledInPlan: boolean; available: boolean;
  connection: null | { status: 'CONNECTED' | 'NEEDS_REAUTH' | 'DISCONNECTED'; mpUserId: string; nickname: string | null; liveMode: boolean; methods: string[]; connectedAt: string; expiresAt: string | null; lastCheckedAt: string | null; lastError: string | null };
};

const RESULT: Record<string, { tone: 'success' | 'warning' | 'danger'; text: string }> = {
  connected: { tone: 'success', text: 'Conta Mercado Pago conectada. Os pagamentos pelo celular já caem direto na sua conta.' },
  cancelled: { tone: 'warning', text: 'A autorização foi cancelada no Mercado Pago. Nada foi alterado.' },
  test_account_refused: { tone: 'danger', text: 'Essa é uma conta de teste do Mercado Pago e não pode ser usada para cobranças reais.' },
  error: { tone: 'danger', text: 'Não foi possível concluir a conexão. Tente de novo.' },
};

/** Conexão da conta Mercado Pago do RESTAURANTE (OAuth). O Garlic nunca pede a senha do Mercado Pago nem exibe tokens. */
export function StaffPaymentsIntegration({ restaurant }: { restaurant: TenantRestaurant }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['staff-mp'], queryFn: () => api<Integration>('/api/staff/integrations/mercadopago') });
  const [busy, setBusy] = useState<string | null>(null);
  const result = new URLSearchParams(location.search).get('mp');
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  const c = d.connection;
  const connected = c?.status === 'CONNECTED';

  return (
    <>
      <PageHeader title="Pagamentos online" description="Pix e cartão pelo celular do cliente, recebidos direto na conta Mercado Pago do restaurante." />
      {result && RESULT[result] && <div className="mb-6"><Alert tone={RESULT[result]!.tone}>{RESULT[result]!.text}</Alert></div>}
      {!d.platformConfigured && <div className="mb-6"><Alert tone="warning">A integração com o Mercado Pago ainda não foi configurada pela plataforma Garlic.</Alert></div>}
      {!d.enabledInPlan && <div className="mb-6"><Alert tone="warning">Pagamentos online não estão habilitados no seu plano. Fale com o Garlic.</Alert></div>}

      <Card>
        <CardHeader
          title="Conta Mercado Pago"
          action={
            connected ? <Badge tone="success" dot>Conectada</Badge> : c?.status === 'NEEDS_REAUTH' ? <Badge tone="danger" dot>Reautorização necessária</Badge> : <Badge>Não conectada</Badge>
          }
        />
        <div className="space-y-4 p-5 text-sm">
          {c && c.status !== 'DISCONNECTED' ? (
            <dl className="grid gap-3 sm:grid-cols-2">
              <div><dt className="text-ink-3">Conta</dt><dd className="font-medium">{c.nickname ?? `ID ${c.mpUserId}`}</dd></div>
              <div><dt className="text-ink-3">Ambiente</dt><dd>{c.liveMode ? <Badge tone="success">Produção</Badge> : <Badge tone="warning">Teste (sem cobranças reais)</Badge>}</dd></div>
              <div><dt className="text-ink-3">Métodos habilitados</dt><dd>{c.methods.length ? c.methods.map((m) => (m === 'PIX' ? 'Pix' : 'Cartão')).join(' e ') : 'Nenhum — verifique sua conta (Pix exige chave cadastrada)'}</dd></div>
              <div><dt className="text-ink-3">Conectada em</dt><dd>{formatDateTime(c.connectedAt, restaurant.timezone)}</dd></div>
              {c.expiresAt && <div><dt className="text-ink-3">Autorização válida até</dt><dd>{formatDateTime(c.expiresAt, restaurant.timezone)} (renovada automaticamente)</dd></div>}
              {c.lastCheckedAt && <div><dt className="text-ink-3">Última verificação</dt><dd>{formatDateTime(c.lastCheckedAt, restaurant.timezone)}</dd></div>}
              {c.lastError && <div className="sm:col-span-2"><Alert tone="danger">{c.lastError}</Alert></div>}
            </dl>
          ) : (
            <p className="text-ink-2">Ao conectar, você será levado ao site do Mercado Pago para autorizar o Garlic a <strong>criar cobranças em nome do restaurante</strong>. Você entra com a sua conta lá — o Garlic nunca vê sua senha — e o dinheiro cai direto na sua conta, sem intermediários.</p>
          )}
          <ul className="space-y-1 text-xs text-ink-3">
            <li className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-sprout-600" /> Cada pagamento é confirmado pelo Garlic consultando o Mercado Pago, nunca pela tela do cliente.</li>
            <li className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-sprout-600" /> Tokens de acesso ficam criptografados no servidor e não aparecem em nenhuma tela.</li>
            <li className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-sprout-600" /> Sem conexão, o pagamento com a equipe continua funcionando normalmente.</li>
          </ul>
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            {(!connected) && (
              <Button
                icon={<Link2 className="size-4" />}
                loading={busy === 'connect'}
                disabled={!d.platformConfigured || !d.enabledInPlan}
                onClick={() => run('connect', async () => {
                  const r = await post<{ url: string }>('/api/staff/integrations/mercadopago/connect');
                  window.location.href = r.url;
                })}
              >
                {c?.status === 'NEEDS_REAUTH' ? 'Reautorizar no Mercado Pago' : 'Conectar Mercado Pago'} <ExternalLink className="size-3.5" />
              </Button>
            )}
            {connected && (
              <>
                <Button variant="secondary" icon={<RefreshCw className="size-4" />} loading={busy === 'test'} onClick={() => run('test', async () => {
                  qc.setQueryData(['staff-mp'], await post<Integration>('/api/staff/integrations/mercadopago/test'));
                  toast.success('Conexão verificada');
                })}>Testar conexão</Button>
                <Button variant="ghost" icon={<Unlink className="size-4" />} loading={busy === 'disconnect'} onClick={() => confirm('Desconectar? Os clientes não poderão pagar pelo celular até reconectar. Para revogar de vez, remova o acesso também no painel do Mercado Pago.') && run('disconnect', async () => {
                  qc.setQueryData(['staff-mp'], await post<Integration>('/api/staff/integrations/mercadopago/disconnect'));
                  toast.success('Conta desconectada');
                })}>Desconectar</Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </>
  );
}
